import { useEffect, useState } from 'react';
import { earnings } from '../services/api';
import LayoutV2 from '../components/LayoutV2';
import {
  Badge,
  Button,
  Loading,
  PageHeader,
  SectionBlock
} from '../components/ui';

function getRecentSevenDaysRange() {
  const now = new Date();
  const endDate = now.toISOString().slice(0, 10);
  const start = new Date(now);
  start.setDate(start.getDate() - 7);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate
  };
}

function formatCurrency(value) {
  return `₱${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function formatDateTime(value) {
  if (!value) return '--';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export default function EarningsV2() {
  const [range, setRange] = useState(() => getRecentSevenDaysRange());
  const [settlementStatus, setSettlementStatus] = useState('unsettled');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await earnings.get({
          startDate: range.startDate,
          endDate: range.endDate,
          settlementStatus
        });
        setData(res.data || null);
        setSelectedItem(null);
      } catch (err) {
        console.error(err);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [range.startDate, range.endDate, settlementStatus]);

  const items = data?.items || [];

  return (
    <LayoutV2>
      <PageHeader
        title="Earnings"
      />

      <div className="space-y-6">
        <SectionBlock
          title="Filters"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="label">Start Date</label>
              <input
                type="date"
                value={range.startDate}
                onChange={(e) => setRange((prev) => ({ ...prev, startDate: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="label">End Date</label>
              <input
                type="date"
                value={range.endDate}
                onChange={(e) => setRange((prev) => ({ ...prev, endDate: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="label">Settlement</label>
              <select
                value={settlementStatus}
                onChange={(e) => setSettlementStatus(e.target.value)}
                className="input"
              >
                <option value="unsettled">Unsettled</option>
                <option value="all">All</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-center"
                onClick={() => setRange(getRecentSevenDaysRange())}
              >
                Reset 7 Days
              </Button>
            </div>
          </div>
        </SectionBlock>

        <SectionBlock
          title="Total Amount"
        >
          <div className="mb-4 text-2xl font-semibold text-emerald-200">{formatCurrency(data?.totalAmount || 0)}</div>
          {loading ? (
            <Loading text="Loading earnings..." />
          ) : items.length === 0 ? (
            <div className="rounded-[20px] border border-dashed border-white/10 bg-slate-950/20 p-6 text-sm text-slate-400">
              No earnings found in the current date range.
            </div>
          ) : (
            <>
              <div className="hidden lg:block table-container">
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Amount</th>
                        <th>Currency</th>
                        <th>Generated Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr
                          key={item.id}
                          onClick={() => setSelectedItem(item)}
                          className={`cursor-pointer ${item.inSettlementProcess ? 'opacity-70' : ''}`}
                        >
                          <td className="font-mono">{item.id}</td>
                          <td>{formatCurrency(item.amount)}</td>
                          <td>{item.currency || 'PHP'}</td>
                          <td>{formatDateTime(item.generatedTime)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 lg:hidden">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={`rounded-[20px] border border-white/8 bg-white/[0.03] p-4 ${item.inSettlementProcess ? 'opacity-70' : ''}`}
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-mono text-sm text-slate-200">{item.id}</p>
                        <p className="mt-1 text-sm text-slate-400">{formatDateTime(item.generatedTime)}</p>
                      </div>
                      <Badge variant={item.inSettlementProcess ? 'gray' : 'success'}>
                        {item.inSettlementProcess ? 'In settlement' : 'Open'}
                      </Badge>
                    </div>
                    <p className="mt-4 text-lg font-semibold text-emerald-200">{formatCurrency(item.amount)}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </SectionBlock>

        {selectedItem ? (
          <SectionBlock
            title="Earning Detail"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4">
                <p className="label">Customer Mobile</p>
                <p className="text-sm text-slate-100">{selectedItem.customerMobile || '--'}</p>
              </div>
              <div className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4">
                <p className="label">Customer Name</p>
                <p className="text-sm text-slate-100">{selectedItem.customerName || '--'}</p>
              </div>
              <div className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4">
                <p className="label">Loan Product Name</p>
                <p className="text-sm text-slate-100">{selectedItem.loanProductName || '--'}</p>
              </div>
              <div className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4">
                <p className="label">Approved Time</p>
                <p className="text-sm text-slate-100">{formatDateTime(selectedItem.approvedTime)}</p>
              </div>
            </div>
          </SectionBlock>
        ) : null}
      </div>
    </LayoutV2>
  );
}
